import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getBlob, getStorage, ref } from 'firebase/storage';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import colors from '../../assets/theme/colors';
import { getCareSourceDisplayStatus } from './careSourceStatusModel';
import {
  getCareDocumentMeta,
  getCareDocumentOrganizedKnowledgeCards,
  getCareDocumentOriginalText,
  getCareDocumentPurposeLabel,
  getCareDocumentTheme,
  getCareDocumentTitle,
} from './careDocumentPresentation';

const CareDocumentIcon = ({ source }) => {
  const theme = getCareDocumentTheme(source);

  return (
    <Box
      sx={{
        width: 54,
        height: 54,
        borderRadius: '16px',
        display: 'grid',
        placeItems: 'center',
        bgcolor: theme.bg,
        color: theme.ink,
        flexShrink: 0,
      }}
    >
      <Inventory2OutlinedIcon sx={{ fontSize: 26 }} />
    </Box>
  );
};

const getOriginalDocumentAttachment = (source = {}) => [
  ...(Array.isArray(source.documentAttachments) ? source.documentAttachments : []),
  ...(Array.isArray(source.sourceMetadata?.documentAttachments) ? source.sourceMetadata.documentAttachments : []),
  ...(Array.isArray(source.metadata?.source?.documentAttachments) ? source.metadata.source.documentAttachments : []),
].find((attachment) => attachment?.url || attachment?.path || attachment?.storagePath);

const getOriginalImageAttachment = (source = {}) => [
  ...(Array.isArray(source.imageAttachments) ? source.imageAttachments : []),
  ...(Array.isArray(source.sourceMetadata?.imageAttachments) ? source.sourceMetadata.imageAttachments : []),
  ...(Array.isArray(source.metadata?.source?.imageAttachments) ? source.metadata.source.imageAttachments : []),
].find((attachment) => attachment?.url);

const CareDocumentDetailSheet = ({ open = false, source = null, onClose = () => {} }) => {
  const [showOriginal, setShowOriginal] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [originalDocumentUrl, setOriginalDocumentUrl] = useState('');
  const originalDocumentAttachment = getOriginalDocumentAttachment(source || {});
  const originalImageAttachment = getOriginalImageAttachment(source || {});
  useEffect(() => {
    let active = true;
    let objectUrl = '';
    const directUrl = String(originalDocumentAttachment?.url || '').trim();
    const storagePath = String(originalDocumentAttachment?.storagePath || originalDocumentAttachment?.path || '').trim();
    setOriginalDocumentUrl(directUrl);
    if (!directUrl && storagePath) {
      getBlob(ref(getStorage(), storagePath))
        .then((blob) => {
          if (!active) return;
          objectUrl = URL.createObjectURL(blob);
          setOriginalDocumentUrl(objectUrl);
        })
        .catch(() => { if (active) setOriginalDocumentUrl(''); });
    }
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [originalDocumentAttachment?.path, originalDocumentAttachment?.storagePath, originalDocumentAttachment?.url]);

  if (!source) {
    return null;
  }

  const organizedKnowledgeCards = getCareDocumentOrganizedKnowledgeCards(source);
  const originalText = getCareDocumentOriginalText(source);
  const status = getCareSourceDisplayStatus(source);
  const needsReview = status.label === 'Needs your review';
  const toggleGroupExpanded = (groupId) => {
    setExpandedGroups((current) => ({
      ...current,
      [groupId]: !current[groupId],
    }));
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '100vw',
          height: 'auto',
          maxHeight: '86vh',
          borderTopLeftRadius: '30px',
          borderTopRightRadius: '30px',
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          bgcolor: colors.semantic.profileFlow.careStory.pageBg,
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ height: '100%', overflow: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.05 }}>
          <Box sx={{ width: 54, height: 5, borderRadius: 999, bgcolor: colors.semantic.profileFlow.careStory.sheetHandle }} />
        </Box>

        <Box
          sx={{
            px: { xs: 1.55, sm: 2 },
            pt: 1.4,
            pb: 1.35,
            borderBottom: `1px solid ${colors.semantic.line}`,
            display: 'grid',
            gridTemplateColumns: 'auto minmax(0, 1fr) auto',
            gap: 1.1,
            alignItems: 'center',
          }}
        >
          <CareDocumentIcon source={source} />
          <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
            <Typography
              title={getCareDocumentTitle(source)}
              sx={{
                maxWidth: '100%',
                fontSize: { xs: '1.02rem', sm: '1.18rem' },
                fontWeight: 880,
                color: colors.semantic.profileFlow.careStory.text,
                lineHeight: 1.08,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {getCareDocumentTitle(source)}
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.35, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 650, color: alpha(colors.semantic.ink, 0.68), lineHeight: 1.25 }}>
                {getCareDocumentMeta(source)}
              </Typography>
              {getCareDocumentPurposeLabel(source) ? (
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: alpha(colors.semantic.sageDeep, 0.92), lineHeight: 1.25 }}>
                  Purpose: {getCareDocumentPurposeLabel(source)}
                </Typography>
              ) : null}
              {needsReview ? (
                <Chip
                  size="small"
                  label="Extracted details"
                  sx={{
                    height: 22,
                    borderRadius: '999px',
                    bgcolor: colors.semantic.profileFlow.careStory.warningBg,
                    border: `1px solid ${colors.semantic.profileFlow.careStory.warningBorder}`,
                    color: colors.semantic.profileFlow.careStory.warningText,
                    fontSize: '0.7rem',
                    fontWeight: 850,
                  }}
                />
              ) : null}
            </Stack>
          </Box>
          <IconButton
            aria-label="Close care document"
            onClick={onClose}
            sx={{
              width: 46,
              height: 46,
              bgcolor: '#FFFFFF',
              color: colors.semantic.ink,
              boxShadow: `0 4px 14px ${colors.landing.shadowSoft}`,
              '&:hover': { bgcolor: colors.semantic.sageMist },
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        <Stack spacing={1.35} sx={{ px: { xs: 1.55, sm: 2 }, py: 1.6 }}>
          {originalImageAttachment?.url ? (
            <Box
              component="img"
              src={originalImageAttachment.url}
              alt={originalImageAttachment.fileName || originalImageAttachment.filename || 'Original uploaded screenshot'}
              sx={{ width: '100%', maxHeight: '62vh', objectFit: 'contain', borderRadius: '16px', bgcolor: '#FFFFFF' }}
            />
          ) : null}
          {originalDocumentAttachment && originalDocumentUrl ? (
            <Button
              component="a"
              href={originalDocumentUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              sx={{ alignSelf: 'flex-start', borderRadius: '999px', textTransform: 'none', fontWeight: 850 }}
            >
              {`Open ${originalDocumentAttachment.fileName || originalDocumentAttachment.filename || 'original document'}`}
            </Button>
          ) : null}
          {organizedKnowledgeCards.length ? (
            <Box
              sx={{
                p: { xs: 1.25, sm: 1.45 },
                borderRadius: '18px',
                border: `1px solid ${colors.semantic.line}`,
                bgcolor: '#FFFFFF',
              }}
            >
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', color: alpha(colors.semantic.ink, 0.72) }}>
                Care Knowledge Extracted
              </Typography>
              <Stack spacing={1.25} sx={{ mt: 1.15 }}>
                {organizedKnowledgeCards.map((card, cardIndex) => (
                  <Box
                    key={card.id}
                    sx={{
                      pt: cardIndex === 0 ? 0 : 1.05,
                      borderTop: cardIndex === 0 ? 'none' : `1px solid ${alpha(colors.semantic.ink, 0.08)}`,
                    }}
                  >
                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', color: alpha(colors.semantic.ink, 0.58), lineHeight: 1.3 }}>
                      {card.bucketLabel}
                    </Typography>
                    <Typography sx={{ mt: 0.35, fontSize: '0.98rem', fontWeight: 950, color: colors.semantic.profileFlow.careStory.text, lineHeight: 1.25 }}>
                      {card.title}
                      <Box component="span" sx={{ ml: 0.5, color: alpha(colors.semantic.ink, 0.45), fontWeight: 850 }}>
                        ({card.items.length})
                      </Box>
                    </Typography>
                    <Stack spacing={0.65} sx={{ mt: 0.75 }}>
                      {(expandedGroups[card.id] ? card.items : card.items.slice(0, 8)).map((item) => (
                        <Stack key={item.id || item.text} direction="row" spacing={0.7} alignItems="flex-start">
                          <Typography
                            component="span"
                            aria-hidden="true"
                            sx={{
                              mt: 0.08,
                              fontSize: '0.93rem',
                              fontWeight: 900,
                              color: colors.semantic.sageDeep,
                              lineHeight: 1.4,
                            }}
                          >
                            ✓
                          </Typography>
                          <Typography sx={{ fontSize: '0.96rem', color: colors.semantic.ink, lineHeight: 1.4 }}>
                            {item.text}
                          </Typography>
                        </Stack>
                      ))}
                      {card.items.length > 8 ? (
                        <Button
                          variant="text"
                          onClick={() => toggleGroupExpanded(card.id)}
                          sx={{
                            alignSelf: 'flex-start',
                            minWidth: 0,
                            px: 0,
                            py: 0.15,
                            pl: 1.6,
                            color: colors.semantic.sageDeep,
                            fontSize: '0.9rem',
                            fontWeight: 850,
                            textTransform: 'none',
                            '&:hover': {
                              bgcolor: 'transparent',
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          {expandedGroups[card.id] ? 'Show less' : `Show ${card.items.length - 8} more`}
                        </Button>
                      ) : null}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>
          ) : null}

          {originalText ? (
            <Box>
              <Button
                variant="text"
                onClick={() => setShowOriginal((current) => !current)}
                startIcon={<ExpandMoreRoundedIcon sx={{ transform: showOriginal ? 'rotate(180deg)' : 'rotate(-90deg)' }} />}
                sx={{
                  px: 0,
                  color: colors.semantic.sageDeep,
                  fontSize: '0.96rem',
                  fontWeight: 850,
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: 'transparent',
                    textDecoration: 'underline',
                  },
                }}
              >
                {showOriginal ? 'Hide original document' : 'View original document'}
                <Box component="span" sx={{ ml: 0.8, color: alpha(colors.semantic.ink, 0.58), fontWeight: 700 }}>
                  · kept word-for-word
                </Box>
              </Button>

              {showOriginal ? (
                <Box
                  sx={{
                    mt: 0.75,
                    p: 1.45,
                    borderRadius: '18px',
                    border: `1px solid ${colors.semantic.line}`,
                    bgcolor: '#FFFFFF',
                  }}
                >
                  <Typography
                    component="pre"
                    sx={{
                      m: 0,
                      fontFamily: 'inherit',
                      fontSize: '0.94rem',
                      color: colors.semantic.ink,
                      lineHeight: 1.55,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {originalText}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          ) : null}
        </Stack>
      </Box>
    </Drawer>
  );
};

export default CareDocumentDetailSheet;
